import React from "react";
import { CREST } from "@/constants/testIds";

export const STEPS = ["Welcome", "Contact", "Sizing", "Style", "Categories", "Brands", "Finish"];

const SIZING_FIELDS = [
  { key: "shirt", label: "Shirt", placeholder: "S / M / L / XL" },
  { key: "hat", label: "Hat", placeholder: "S-M / L-XL / 7 1/4" },
  { key: "outerwear", label: "Outerwear", placeholder: "S / M / L" },
  { key: "shoe", label: "Shoe", placeholder: "10.5 / 11" },
  { key: "belt", label: "Belt", placeholder: "34 / 36" },
];

// --- Step panes ---
// Each step is a small dumb component that takes `form`, mutators, and
// option lists. Keeps OnboardingWizard.jsx focused on shell + orchestration.

export function WelcomeStep({ user }) {
  return (
    <div className="space-y-4">
      <p className="text-[#1A1A1A]">
        Welcome, <span className="font-serif text-xl">{user?.name?.split(" ")[0] || "Member"}</span>.
      </p>
      <p className="text-sm text-[#4A4A4A] leading-relaxed">
        Your <em>Crest Profile</em> helps the pro shop know what to source for you — your
        preferred sizes, the brands you trust, and the categories you care about. It takes
        about a minute, and you can edit any part later from your profile.
      </p>
      <p className="text-xs text-[#7A7A7A]">All fields are optional. Skip what you&rsquo;d rather not answer.</p>
    </div>
  );
}

export function ContactStep({ form, setForm }) {
  return (
    <div className="space-y-5">
      <div>
        <label className="label-elegant block mb-2" htmlFor="onb-mobile">Mobile Number</label>
        <input
          id="onb-mobile"
          type="tel"
          value={form.mobile_number}
          onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
          data-testid={CREST.mobileInput}
          className="input-field"
          placeholder="(555) 123-4567"
        />
        <p className="text-xs text-[#7A7A7A] mt-2">Only used by the pro shop for time-sensitive updates.</p>
      </div>
    </div>
  );
}

export function SizingStep({ form, setForm }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {SIZING_FIELDS.map((f) => (
        <div key={f.key}>
          <label className="label-elegant block mb-2" htmlFor={`onb-size-${f.key}`}>{f.label}</label>
          <input
            id={`onb-size-${f.key}`}
            type="text"
            value={form.sizing[f.key] || ""}
            onChange={(e) =>
              setForm({ ...form, sizing: { ...form.sizing, [f.key]: e.target.value } })
            }
            data-testid={`${CREST.sizingPrefix}${f.key}`}
            placeholder={f.placeholder}
            className="input-field"
          />
        </div>
      ))}
    </div>
  );
}

export function StyleStep({ form, toggle, styles }) {
  return (
    <ChipGrid
      options={styles}
      selected={form.style_preferences}
      onToggle={(v) => toggle("style_preferences", v)}
      testIdPrefix={CREST.stylePrefix}
    />
  );
}

export function CategoriesStep({ form, toggle, categories }) {
  return (
    <ChipGrid
      options={categories}
      selected={form.favorite_categories}
      onToggle={(v) => toggle("favorite_categories", v)}
      testIdPrefix={CREST.categoryPrefix}
    />
  );
}

export function BrandsStep({ form, toggle, brands, brandOther, setBrandOther, addBrandOther }) {
  // Brands typed by the member that aren't in the curated `brands` list
  // appear as removable chips below the input.
  const customBrands = (form.favorite_brands || []).filter((b) => !brands.includes(b));
  return (
    <div>
      <ChipGrid
        options={brands}
        selected={form.favorite_brands}
        onToggle={(v) => toggle("favorite_brands", v)}
        testIdPrefix={CREST.brandPrefix}
      />
      <div className="mt-6 pt-5 border-t border-[#E5E3D8]">
        <label className="label-elegant block mb-2">Add Another Brand</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={brandOther}
            onChange={(e) => setBrandOther(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBrandOther(); } }}
            placeholder="e.g. Burberry"
            data-testid={CREST.brandOtherInput}
            className="input-field flex-1"
          />
          <button
            type="button"
            onClick={addBrandOther}
            data-testid={CREST.brandOtherAdd}
            className="btn-secondary text-xs"
          >
            Add
          </button>
        </div>
        {customBrands.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {customBrands.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-2 px-3 py-1 text-xs border border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
              >
                {b}
                <button
                  type="button"
                  onClick={() => toggle("favorite_brands", b)}
                  className="text-[#1A422D] hover:text-[#A33]"
                  aria-label={`Remove ${b}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function FinishStep({ form }) {
  // Flatten the sizing dict to a readable one-line summary (e.g. "Shirt L · Hat 7 1/4 · Shoe 11").
  const sizing = Object.entries(form.sizing || {})
    .filter(([, v]) => v)
    .map(([k, v]) => `${k[0].toUpperCase()}${k.slice(1)} ${v}`)
    .join(" · ");
  return (
    <div>
      <p className="text-sm text-[#4A4A4A] mb-4">
        Here&rsquo;s what we&rsquo;ll save to your Crest Profile. You can edit anything later from your profile page.
      </p>
      <dl>
        <ReviewRow label="Mobile" value={form.mobile_number} />
        <ReviewRow label="Sizes" value={sizing} />
        <ReviewRow label="Styles" value={(form.style_preferences || []).join(", ")} />
        <ReviewRow label="Categories" value={(form.favorite_categories || []).join(", ")} />
        <ReviewRow label="Brands" value={(form.favorite_brands || []).join(", ")} />
      </dl>
    </div>
  );
}

// --- Shared helpers ---

function ChipGrid({ options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((opt) => {
        const isOn = (selected || []).some((v) => v.toLowerCase() === opt.toLowerCase());
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            data-testid={`${testIdPrefix}${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            aria-pressed={isOn}
            className={`px-4 py-2 text-sm border transition-colors ${
              isOn
                ? "border-[#1A422D] bg-[#1A422D] text-[#F9F8F4]"
                : "border-[#E5E3D8] bg-white text-[#1A1A1A] hover:border-[#1A422D] hover:text-[#1A422D]"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-[#E5E3D8] last:border-b-0">
      <dt className="label-elegant col-span-1">{label}</dt>
      <dd className="col-span-2 text-[#1A1A1A] text-sm">{value || <span className="text-[#9A9A9A]">—</span>}</dd>
    </div>
  );
}
